import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2, AlertTriangle, Lock, Calendar, Mail, BookOpen, FileCheck2, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import PricingCard from "../components/wealth/PricingCard";
import { PRODUCT_NAME, TOOLKIT_NAME, LAUNCH_PRICE, COMPLETE_VALUE, LAUNCH_COPY, CTA_BUTTON_LABEL, CTA_SUBTEXT, COMPLIANCE_DISCLAIMER, formatUSD } from "../lib/wealthPricing";

/**
 * Post-booking upsell page for the annuity-review flow.
 *
 * Two states driven by query param:
 *   • default                       — Show the Toolkit upsell (one-click → Stripe)
 *   • ?session_id=cs_test_…         — Polling for payment confirmation, then success view
 *
 * "Toolkit" = the same paid Retirement Readiness Scorecard™ product, positioned
 * here as a pre-appointment preparation aid (different framing, same backend SKU).
 */
export default function PrepareReview() {
    const location = useLocation();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const lead = location.state || {};
    const leadId = lead.leadId || params.get("lead_id");

    if (sessionId) {
        return <PostPaymentConfirmation sessionId={sessionId} leadId={leadId} firstName={lead.name?.split(" ")[0]} />;
    }

    return <UpsellPage lead={lead} leadId={leadId} navigate={navigate} />;
}

function UpsellPage({ lead, leadId, navigate }) {
    const [loading, setLoading] = useState(false);
    const firstName = (lead.name || "").split(" ")[0];

    async function buyToolkit() {
        if (!leadId) {
            toast.error("We couldn't link your appointment to a lead. Please refresh and try again.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/wealth/start-from-lead", {
                lead_id: leadId,
                origin_url: window.location.origin,
            });
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Could not start checkout. Please try again.");
                setLoading(false);
            }
        } catch (e) {
            toast.error(formatApiError(e));
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/80 text-white py-16 px-6" data-testid="prepare-review-page">
            <div className="max-w-5xl mx-auto">
                {/* Confirmation banner */}
                <div className="text-center" data-testid="appt-confirmed-banner">
                    <div className="inline-flex items-center gap-2 bg-emerald-400/15 border border-emerald-400/30 rounded-full px-4 py-1.5 text-emerald-200 text-xs font-bold tracking-wide">
                        <CheckCircle2 className="w-3.5 h-3.5" /> APPOINTMENT CONFIRMED
                    </div>
                    <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] font-semibold">
                        Prepare For<br/>Your Review<span className="text-amber-300">.</span>
                    </h1>
                    <p className="mt-6 text-lg text-blue-50/85 leading-relaxed max-w-2xl mx-auto">
                        {firstName ? `${firstName}, your appointment is locked in.` : "Your appointment is locked in."} You'll receive a calendar invite shortly.
                    </p>
                </div>

                {/* Upsell hero card */}
                <div className="mt-12 bg-white text-emerald-950 rounded-2xl shadow-2xl overflow-hidden grid lg:grid-cols-5">
                    {/* Pitch */}
                    <div className="lg:col-span-3 p-8 sm:p-10">
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-700 font-bold">
                            <Sparkles className="w-3.5 h-3.5" /> Recommended Before Your Review
                        </div>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-emerald-950 leading-tight font-semibold">
                            Get more value from your upcoming review.
                        </h2>
                        <p className="mt-5 text-base text-emerald-900/80 leading-relaxed" data-testid="post-booking-launch-copy">
                            {LAUNCH_COPY}
                        </p>
                        <p className="mt-3 text-sm text-emerald-900 leading-relaxed">
                            Complete the assessment before your appointment so your advisor arrives with your personalized scorecard and educational guides in hand — your call skips the basics and starts where it matters most for you.
                        </p>

                        <ul className="mt-6 space-y-3 text-sm" data-testid="toolkit-benefits">
                            {[
                                { icon: FileCheck2, t: "Personalized 0–100 retirement readiness score" },
                                { icon: BookOpen, t: "Printable PDF-style report — bring it to your review" },
                                { icon: Calendar, t: "Your advisor reviews it before the call (no wasted minutes)" },
                            ].map((b, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <b.icon className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                                    <span className="text-emerald-950/90">{b.t}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-7 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                            <button
                                onClick={buyToolkit}
                                disabled={loading}
                                data-testid="toolkit-buy-cta"
                                className="inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 disabled:bg-emerald-900/40 disabled:cursor-not-allowed text-amber-300 px-7 py-4 rounded-lg font-bold text-base shadow-lg shadow-emerald-900/20 transition-all"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                                {loading ? "Redirecting to Stripe…" : CTA_BUTTON_LABEL}
                            </button>
                            <button
                                onClick={() => navigate("/results", { state: lead })}
                                data-testid="toolkit-skip"
                                className="text-emerald-900 hover:text-emerald-900 text-sm font-medium px-3 py-2"
                            >
                                Skip for now
                            </button>
                        </div>
                        <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-emerald-900" data-testid="post-booking-subtext">
                            <Lock className="w-3 h-3" /> {CTA_SUBTEXT}
                        </div>
                    </div>

                    {/* Pricing card */}
                    <div className="lg:col-span-2 bg-[#fdfbf6] p-8 sm:p-10 border-t lg:border-t-0 lg:border-l border-emerald-900/10">
                        <PricingCard variant="hero" />
                    </div>
                </div>

                {/* Reassurance */}
                <div className="mt-10 max-w-2xl mx-auto bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5 text-center">
                    <p className="text-sm text-blue-50/85 leading-relaxed">
                        Already a Toolkit owner? Just bring your scorecard to the appointment.<br className="hidden sm:block"/>
                        Questions before your review? Email <a href="mailto:reviews@yourannuityreview.com" className="text-amber-300 font-semibold hover:text-amber-200">reviews@yourannuityreview.com</a>.
                    </p>
                </div>

                {/* Educational disclaimer */}
                <p className="mt-8 text-center text-[11px] text-blue-100/55 max-w-3xl mx-auto leading-relaxed" data-testid="post-booking-disclaimer">
                    {LAUNCH_COPY} {COMPLIANCE_DISCLAIMER}
                </p>
            </div>
        </div>
    );
}

function PostPaymentConfirmation({ sessionId, leadId, firstName }) {
    const [status, setStatus] = useState("polling"); // polling | paid | error
    const [attempts, setAttempts] = useState(0);

    const poll = useCallback(
        async (n) => {
            if (n >= 10) {
                setStatus("error");
                return;
            }
            try {
                const { data } = await api.get(`/wealth/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (data.status === "expired") {
                    setStatus("error");
                    return;
                }
                setAttempts(n + 1);
                setTimeout(() => poll(n + 1), 2000);
            } catch (e) {
                if (e?.response?.status === 404) {
                    setStatus("error");
                    return;
                }
                setAttempts(n + 1);
                setTimeout(() => poll(n + 1), 2500);
            }
        },
        [sessionId],
    );

    useEffect(() => {
        poll(0);
    }, [poll]);

    if (status === "polling") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-navy-900 text-white px-6">
                <div className="text-center max-w-md" data-testid="prepare-confirmation-polling">
                    <Loader2 className="w-10 h-10 text-amber-300 animate-spin mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl">Confirming your Toolkit purchase…</h2>
                    <p className="mt-3 text-sm text-blue-100/70">Attempt {attempts + 1}</p>
                </div>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-navy-900 text-white px-6">
                <div className="text-center max-w-md" data-testid="prepare-confirmation-error">
                    <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl">We hit a snag</h2>
                    <p className="mt-3 text-sm text-blue-100/70">
                        If you were charged, your Toolkit will arrive by email shortly. Otherwise no charge was made.
                    </p>
                    <Link to="/prepare-review" className="mt-6 inline-flex items-center gap-2 bg-amber-300 text-navy-900 px-5 py-3 rounded-lg font-bold text-sm">Try again</Link>
                </div>
            </div>
        );
    }

    // PAID
    return (
        <div className="min-h-screen bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/80 text-white py-16 px-6" data-testid="prepare-confirmation-paid">
            <div className="max-w-2xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-emerald-400/15 border border-emerald-400/30 rounded-full px-4 py-1.5 text-emerald-200 text-xs font-bold tracking-wide">
                    <CheckCircle2 className="w-3.5 h-3.5" /> TOOLKIT ADDED · PAYMENT CONFIRMED
                </div>
                <h1 className="mt-6 font-serif text-4xl sm:text-5xl text-white leading-tight font-semibold">
                    You're set, {firstName || "friend"}.
                </h1>
                <p className="mt-5 text-base text-blue-50/85 leading-relaxed">
                    Your Retirement Readiness Toolkit is on its way. Watch your inbox in the next minute for the
                    assessment link — complete it before your appointment so your advisor arrives prepared with your personalized scorecard.
                </p>

                <div className="mt-10 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-7 text-left">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300 font-bold">What happens next</div>
                    <ol className="mt-4 space-y-3 text-sm text-blue-50/85" data-testid="next-steps">
                        <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-300 text-navy-900 text-xs font-bold flex items-center justify-center">1</span><span>Open the assessment email (subject: <em>"Your Retirement Readiness Toolkit is ready"</em>).</span></li>
                        <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-300 text-navy-900 text-xs font-bold flex items-center justify-center">2</span><span>Complete the 5-minute assessment. Your printable scorecard arrives instantly.</span></li>
                        <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-300 text-navy-900 text-xs font-bold flex items-center justify-center">3</span><span>Bring (or screenshot) the scorecard to your appointment. Done.</span></li>
                    </ol>
                </div>

                <div className="mt-8 inline-flex items-center gap-2 text-xs text-blue-100/60">
                    <Mail className="w-3.5 h-3.5" /> Need to resend the link? Email reviews@yourannuityreview.com
                </div>
            </div>
        </div>
    );
}
